import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton, Stack, Chip } from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { CycleTimeReport } from '../../../types/reporting';

interface CycleTimeChartProps {
  data?: CycleTimeReport;
  loading?: boolean;
}

const CycleTimeChart: React.FC<CycleTimeChartProps> = ({ data, loading = false }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="40%" height={28} />
          <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const stages = (data?.stages ?? []).map((s) => ({
    label: `${s.fromStage} → ${s.toStage}`,
    averageDays: s.averageDays,
    medianDays: s.medianDays,
    jobCount: s.jobCount,
  }));

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Cycle Time (days)</Typography>
          {data && (
            <Chip
              size="small"
              label={`${data.totalJobsWithData} jobs with data`}
              variant="outlined"
            />
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          From the Progress/Status form milestones.
        </Typography>
        <Box sx={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={stages} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickLine={{ stroke: theme.palette.divider }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                tickLine={{ stroke: theme.palette.divider }}
                label={{
                  value: 'Days',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: theme.palette.text.secondary, fontSize: 12 },
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 4,
                }}
                labelStyle={{ color: theme.palette.text.primary }}
                formatter={(value: number, _name: string, entry: any) => [
                  `${value} days (median ${entry.payload.medianDays} · ${entry.payload.jobCount} jobs)`,
                  'Average',
                ]}
              />
              <Bar dataKey="averageDays" name="Average" fill={theme.palette.primary.main} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CycleTimeChart;
